import React, { useRef, useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import Heading from "@/components/Heading";
import { api } from "@/services/api";
import cepPromise from "cep-promise";
import { equalTo, validateCnpj, validatePassword, maskPhone } from "@/utils/validations";
import { Form } from "@unform/web";
import { Scope } from "@unform/core";
import * as Yup from "yup";
import Input from "@/components/Input";
import InputMask from "@/components/InputMask";
import InputSelect from "@/components/inputSelect";
import ModalPopup from "@/components/ModalPopup";

import "bootstrap/dist/css/bootstrap-grid.min.css";
import { Container, LabelContainer, GroupInput } from "./styles";
import InputSelectDinamic from "@/components/InputSelectDinamic";

export const RegisterStore = () => {
	const [submitForm, setSubmitForm] = useState(false);
	const [modalPopup, setModalPopup] = useState(false);
	const [modalContent, setModalContent] = useState({
		title: "",
		content: "",
	});
	const formRef = useRef(null);
	let history = useHistory();
	const [list, setList] = useState(null);
	const [checked, setChecked] = React.useState(true);

	useEffect(() => {
		(async () => {
			const { data } = await api.get("merchant/distributors");
			setList(data);
		})();
	}, []);

	function handleInputComplete() {
		const zip_code = formRef.current.getFieldValue(
			"merchant.address_components.zip_code",
		);
		let value = zip_code.replace(/[-_.]/g, "").length;

		if (value === 8) {
			cepPromise(zip_code.replace(/[-_.]/g, ""))
				.then((res) => {
					formRef.current.setFieldValue(
						"merchant.address_components.address",
						res.street,
					);
					formRef.current.setFieldValue(
						"merchant.address_components.district",
						res.neighborhood,
					);
					formRef.current.setFieldValue(
						"merchant.address_components.city",
						res.city,
					);
					formRef.current.setFieldValue(
						"merchant.address_components.state",
						res.state,
					);
				})
				.catch(console.error);
		}
	}

	async function handleSubmit(data) {
		Yup.addMethod(Yup.string, "equalTo", equalTo);
		Yup.addMethod(Yup.string, "validatePassword", validatePassword);
		Yup.addMethod(Yup.string, "validateCnpj", validateCnpj);

		try {
			// esquema de validacao
			const schema = Yup.object().shape({
				merchant: Yup.object().shape({
					store: Yup.string().required("Campo obrigatório"),
					manager: Yup.string().required("Campo obrigatório"),
					distributor: Yup.string().required("Campo obrigatório"),
					email: Yup.string()
						.email("E-mail inválido")
						.required("Campo obrigatório"),
					emailConfirm: Yup.string()
						.email("E-mail inválido")
						.equalTo(Yup.ref("email"), "E-mails não correspondem")
						.required("Campo obrigatório"),
					password: Yup.string()
						.validatePassword(
							"A senha deve conter pelo menos 8 caracteres, sendo uma letra maiúscula, uma letra minúscula e um número",
						)
						.required("Campo obrigatório"),
					passwordConfirmation: Yup.string()
						.oneOf([Yup.ref('password'), null], 'As senhas não correspondem')
						.required("Campo obrigatório"),
					cnpj: Yup.string()
						.validateCnpj("CNPJ inválido")
						.required("Campo obrigatório"),
					phone: Yup.string().required("Campo obrigatório"),
					address_components: Yup.object().shape({
						zip_code: Yup.string().required("Campo obrigatório"),
						address: Yup.string().required("Campo obrigatório"),
						number: Yup.number()
							.typeError("Apenas números")
							.positive("Não pode ser 0 ou valor negativo")
							.integer("Apenas números inteiros")
							.required("Campo obrigatório"),
						district: Yup.string().required("Campo obrigatório"),
						city: Yup.string().required("Campo obrigatório"),
						state: Yup.string().required("Campo obrigatório"),
					}),
				}),
			});

			await schema.validate(data, {
				abortEarly: false,
			});

			handleRegisterStore(data);
			formRef.current.setErrors({});
		} catch (err) {
			if (err instanceof Yup.ValidationError) {
				const errorMessages = {};

				err.inner.forEach((error) => {
					errorMessages[error.path] = error.message;
				});

				formRef.current.setErrors(errorMessages);
			}
		}
	}

	function handleRegisterStore(data) {
		setSubmitForm(true);

		api.post("merchant/signup", data)
			.then((res) => {
				setSubmitForm(false);

				if (res.data.status === 500) {
					console.warn(res);
					setModalPopup(true);
					setModalContent({
						title: "Ocorreu um problema!",
						content:
							"Ops! Não foi possível atender sua solicitação.",
					});
				}

				if (res.data.status === 400) {
					setModalPopup(true);
					setModalContent({
						title: "Ocorreu um problema!",
						content: res.data.message,
					});
				}

				if (res.data.status === 200) {
					history.push(`${process.env.PUBLIC_URL}/cadastro-sucesso/merchant`);
				}
			})
			.catch((error) => {
				setSubmitForm(false);
				console.error({ endPoint: "merchant/signup", error });
				setModalPopup(true);
				setModalContent({
					title: "Ocorreu um problema!",
					content: "Ops! Não foi possível atender sua solicitação.",
				});
			});
	}

	return (
		<Container>
			<ModalPopup
				visible={modalPopup}
				message={modalContent}
				onClick={() => setModalPopup(!modalPopup)}
			/>
			<Heading>
				<p className='title'>
					Cadastro <span>lojista</span>
				</p>
			</Heading>

			<section className='container-fluid'>
				<div className='row justify-content-lg-center'>
					<Form
						ref={formRef}
						name='registerStore'
						onSubmit={handleSubmit}
						className='col-12 col-lg-7'>
						<h2 className='desktop-title'>Dados da loja</h2>
						<Scope path='merchant'>
							<LabelContainer>
								<InputMask
									label='CNPJ*'
									mask='99.999.999/9999-99'
									name='cnpj'
									placeholder='00.000.000/0001-00'
								/>
							</LabelContainer>

							<LabelContainer>
								<Input
									label='Nome da loja*'
									name='store'
									placeholder='Digite o nome da loja'
								/>
							</LabelContainer>

							<LabelContainer>
								<Input
									label='Responsável*'
									name='manager'
									placeholder='Digite o nome completo'
								/>
							</LabelContainer>

							<InputSelectDinamic
								label='Distribuidor'
								name='distributor'
								data={list?.distributors}
							/>

							<LabelContainer>
								<Input
									label='E-mail*'
									name='email'
									placeholder='exemplo@exemplo.com'
								/>
							</LabelContainer>

							<LabelContainer>
								<Input
									label='Confirme seu e-mail*'
									name='emailConfirm'
									placeholder='exemplo@exemplo.com'
								/>
							</LabelContainer>

							<LabelContainer>
								<Input
									label='Senha*'
									name='password'
									type='password'
									placeholder='Digite uma senha'
								/>
							</LabelContainer>

							<LabelContainer>
								<Input
									type='password'
									label='Confirmar senha*'
									name='passwordConfirmation'
									placeholder='Re-digite sua senha'
								/>
							</LabelContainer>

							<LabelContainer>
								<Input
									type='text'
									label='Telefone*'
									name='phone'
									placeholder='(00) 00000-0000'
									onChange={maskPhone}
									maxLength="15"
								/>
							</LabelContainer>

							<Scope path='address_components'>
								<GroupInput className='flex-center'>
									<LabelContainer>
										<InputMask
											label='CEP*'
											name='zip_code'
											mask='99999-999'
											placeholder='00000-000'
											onChange={handleInputComplete}
										/>
									</LabelContainer>
									<a
										href='//www.buscacep.correios.com.br/sistemas/buscacep/'
										target='_blank'
										rel='noopener noreferrer'
										className='text-spotlight'>
										Não sei o CEP
									</a>
								</GroupInput>

								<LabelContainer>
									<Input
										label='Endereço*'
										name='address'
										placeholder='Digite seu endereço'
									/>
								</LabelContainer>

								<GroupInput>
									<LabelContainer>
										<Input
											label='Número*'
											name='number'
											placeholder='ex: 1234'
										/>
									</LabelContainer>
									<LabelContainer>
										<Input
											label='Complemento'
											name='complement'
											placeholder='ex: casa, depto, sala'
										/>
									</LabelContainer>
								</GroupInput>

								<LabelContainer>
									<Input
										label='Bairro*'
										name='district'
										placeholder='Digite seu bairro'
									/>
								</LabelContainer>

								<GroupInput>
									<LabelContainer>
										<Input
											label='Cidade*'
											name='city'
											placeholder='Digite sua cidade'
										/>
									</LabelContainer>
									<LabelContainer>
										<InputSelect
											label='Estado*'
											name='state'
										/>
									</LabelContainer>
								</GroupInput>
							</Scope>
						</Scope>

						<label className='termsOfUser'>
							<input
								type='checkbox'
								name='checkbox'
								required
								checked={checked}
								onChange={() => setChecked(!checked)}
							/>
							<p>
								Estou de acordo com os{" "}
								<Link to={`${process.env.PUBLIC_URL}/regulamento`} className='text-spotlight'>
									termos e condições
								</Link>{" "}
								do programa.
							</p>
						</label>

						<p>* Campos obrigatórios.</p>
						<button
							disabled={submitForm ? "disabled" : ""}
							type='submit'
							className='btn-primary submit-form'>
							{submitForm
								? "Enviando cadastro"
								: "enviar cadastro"}
						</button>
						<p className='link-login'>
							Já possuo cadastro.{" "}
							<Link to={`${process.env.PUBLIC_URL}/login/merchant`}>Fazer login</Link>
						</p>
					</Form>
				</div>
			</section>
		</Container>
	);
};

export default RegisterStore;
